import { useCallback, useEffect, useState } from 'react'
import { complianceApiService } from '../../services/complianceApiService'
import type { ComplianceControlRow, ComplianceDashboard, ComplianceTask, ControlStatus } from '../../services/complianceApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import AdminTabs from './AdminTabs'

const STATUS_COLOR: Record<ControlStatus, string> = { green: colors.success, amber: colors.warning, red: colors.error }

function day(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : '—'
}

const card: React.CSSProperties = {
  background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md,
}

/**
 * Super-admin Compliance Center (SOC 2 CC4). The scheduler opens tasks and pushes/emails the operator,
 * so this page is where a reminder lands: do the review, record notes or attach evidence, close it.
 */
export default function AdminCompliancePage() {
  const [dash, setDash] = useState<ComplianceDashboard | null>(null)
  const [pending, setPending] = useState<ComplianceTask[]>([])
  const [history, setHistory] = useState<ComplianceTask[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError('')
    try {
      const [d, p, done, waived] = await Promise.all([
        complianceApiService.dashboard(),
        complianceApiService.tasks('pending'),
        complianceApiService.tasks('done'),
        complianceApiService.tasks('waived'),
      ])
      setDash(d)
      setPending([...p].sort((a, b) => a.dueAt.localeCompare(b.dueAt)))
      setHistory([...done, ...waived].sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '')))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load the Compliance Center')
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function act(fn: () => Promise<unknown>) {
    setBusy(true)
    setError('')
    try { await fn(); await load() }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Action failed') }
    finally { setBusy(false) }
  }

  const [since, setSince] = useState(() => new Date(Date.now() - 365 * 864e5).toISOString().slice(0, 10))
  const [until, setUntil] = useState(() => new Date(Date.now() + 864e5).toISOString().slice(0, 10))

  function save(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = name
    a.click()
    URL.revokeObjectURL(url)
  }

  async function exportPackage() {
    setBusy(true)
    setError('')
    try {
      save(await complianceApiService.exportPackage(`${since}T00:00:00Z`, `${until}T00:00:00Z`),
        `docmorphia-soc2-evidence-${since}-${until}.zip`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export failed')
    } finally {
      setBusy(false)
    }
  }

  async function download(task: ComplianceTask) {
    try {
      save(await complianceApiService.evidence(task.complianceTaskId), task.evidenceFileName ?? 'evidence')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Download failed')
    }
  }

  return (
    <div>
      <AdminTabs />
      <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.md }}>Compliance Center</h1>
      {error && <div role="alert" style={{ color: colors.error, marginBottom: spacing.md }}>{error}</div>}

      {dash && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: spacing.md, marginBottom: spacing.lg }}>
          <Stat label="Open tasks" value={dash.openTasks} />
          <Stat label="Overdue" value={dash.overdueTasks} alert={dash.overdueTasks > 0} />
          <Stat label="Failing or silent checks" value={dash.failingChecks} alert={dash.failingChecks > 0} />
        </div>
      )}

      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, margin: `${spacing.lg} 0 ${spacing.sm}` }}>Needs you</h2>
      {pending.length === 0
        ? <div style={{ ...card, color: colors.textSecondary }}>Nothing due. You will be notified when something is.</div>
        : pending.map(t => (
          <TaskCard key={t.complianceTaskId} task={t} control={dash?.controls.find(c => c.controlKey === t.controlKey)} busy={busy}
            onComplete={(notes, file) => act(() => complianceApiService.complete(t.complianceTaskId, notes, file))}
            onWaive={reason => act(() => complianceApiService.waive(t.complianceTaskId, reason))} />
        ))}

      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, margin: `${spacing.lg} 0 ${spacing.sm}` }}>Controls</h2>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <thead>
            <tr style={{ textAlign: 'left', color: colors.textSecondary }}>
              {['', 'Control', 'Criteria', 'Cadence', 'Latest'].map(h => <th key={h} style={{ padding: spacing.sm }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {dash?.controls.map(c => (
              <tr key={c.controlKey} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: spacing.sm }}>
                  <span aria-label={c.status} title={c.status} style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: STATUS_COLOR[c.status] }} />
                </td>
                <td style={{ padding: spacing.sm }}>
                  <div style={{ fontWeight: 600, color: colors.text }}>{c.controlKey} · {c.title}</div>
                  <div style={{ color: colors.textSecondary }}>{c.description}</div>
                </td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>{c.criteria}</td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>{c.kind === 'automated' ? 'Automated' : c.frequency}</td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>
                  {c.latestRun ? `${c.latestRun.result} · ${day(c.latestRun.finishedAt)}` : c.openTask ? `${c.openTask.periodKey} due ${day(c.openTask.dueAt)}` : c.kind === 'automated' ? 'Never reported' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, margin: `${spacing.lg} 0 ${spacing.sm}` }}>Auditor package</h2>
      <div style={{ ...card, display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>From <input type="date" value={since} onChange={e => setSince(e.target.value)} /></label>
        <label style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>To <input type="date" value={until} onChange={e => setUntil(e.target.value)} /></label>
        <button type="button" disabled={busy} onClick={exportPackage}
          style={{ padding: `${spacing.xs} ${spacing.md}`, background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>
          Download evidence package
        </button>
        <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Catalog, control-operation calendar, verified compliance log, per-population CSVs, check runs, tasks and evidence, auditor requests and answers, policies, with a SHA-256 manifest.</span>
      </div>

      <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, margin: `${spacing.lg} 0 ${spacing.sm}` }}>History</h2>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <tbody>
            {history.length === 0 && <tr><td style={{ padding: spacing.sm, color: colors.textSecondary }}>No closed tasks yet.</td></tr>}
            {history.map(t => (
              <tr key={t.complianceTaskId} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: spacing.sm, whiteSpace: 'nowrap' }}>{day(t.completedAt)}</td>
                <td style={{ padding: spacing.sm }}>{t.controlKey} · {t.controlTitle} ({t.periodKey})</td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>{t.status === 'waived' ? `Waived: ${t.notes ?? ''}` : t.notes}</td>
                <td style={{ padding: spacing.sm }}>
                  {t.evidenceFileName && <button type="button" onClick={() => download(t)} style={linkButton}>{t.evidenceFileName}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const linkButton: React.CSSProperties = { background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', padding: 0 }

function Stat({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div style={card}>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{label}</div>
      <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: alert ? colors.error : colors.text }}>{value}</div>
    </div>
  )
}

function TaskCard({ task, control, busy, onComplete, onWaive }: {
  task: ComplianceTask
  control?: ComplianceControlRow
  busy: boolean
  onComplete: (notes: string, file: File | null) => void
  onWaive: (reason: string) => void
}) {
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const overdue = new Date(task.dueAt) <= new Date()
  return (
    <div style={{ ...card, marginBottom: spacing.sm, borderColor: overdue ? colors.error : colors.border }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.md, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600, color: colors.text }}>{task.controlKey} · {task.controlTitle} ({task.periodKey})</div>
        <div style={{ color: overdue ? colors.error : colors.textSecondary, fontSize: fontSize.sm }}>{overdue ? 'Overdue — ' : ''}due {day(task.dueAt)}</div>
      </div>
      {control && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, margin: `${spacing.xs} 0` }}>
        {control.description}{control.evidenceHint ? ` Evidence: ${control.evidenceHint}.` : ''}{control.docPath ? ` See ${control.docPath}.` : ''}
      </div>}
      <textarea aria-label={`Notes for ${task.controlKey}`} value={notes} onChange={e => setNotes(e.target.value)} rows={3}
        placeholder="What you reviewed, what you found, any follow-up"
        style={{ width: '100%', boxSizing: 'border-box', marginTop: spacing.sm, padding: spacing.sm, borderRadius: radius.sm, border: `1px solid ${colors.border}` }} />
      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap', marginTop: spacing.sm }}>
        <input type="file" aria-label={`Evidence for ${task.controlKey}`} onChange={e => setFile(e.target.files?.[0] ?? null)} />
        <button type="button" disabled={busy || (!notes.trim() && !file)} onClick={() => onComplete(notes, file)}
          style={{ padding: `${spacing.xs} ${spacing.md}`, background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>
          Mark done
        </button>
        <button type="button" disabled={busy || !notes.trim()} onClick={() => onWaive(notes)} title="Uses the notes as the waiver reason"
          style={{ padding: `${spacing.xs} ${spacing.md}`, background: 'transparent', color: colors.textSecondary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer' }}>
          Waive
        </button>
      </div>
    </div>
  )
}
